import { useId, useState } from "react";
import { api, ApiError, toApiError, unwrap, type JobStatusFilter } from "../../api/client";
import { ErrorMessage, LiveMessage, Loading } from "../../components/ui";
import { t } from "../../i18n/t";
import { formatDateTime, runStatusLabel } from "../../lib/format";
import { useQuery } from "../../lib/useQuery";

const STATUSES: JobStatusFilter[] = ["queued", "running", "succeeded", "failed"];

function payloadSummary(payload: Record<string, unknown>): string {
  return Object.entries(payload)
    .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join(", ");
}

export function JobsTab() {
  const id = useId();
  const [status, setStatus] = useState<JobStatusFilter | "">("");
  const jobs = useQuery(`admin:jobs:${status}`, (signal) =>
    unwrap(
      api.GET("/api/admin/jobs", {
        params: { query: status ? { status, limit: 200 } : { limit: 200 } },
        signal,
      }),
    ),
  );
  const [message, setMessage] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  async function retry(jobId: number) {
    setBusy(jobId);
    setError(null);
    try {
      await unwrap(
        api.POST("/api/admin/jobs/{job_id}/retry", { params: { path: { job_id: jobId } } }),
      );
      setMessage(t("admin.retried", { id: jobId }));
      jobs.reload();
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <LiveMessage message={message} />
      {error && <ErrorMessage error={error} />}
      <div className="toolbar">
        <div className="field">
          <label htmlFor={`${id}-status`}>{t("admin.jobsFilter")}</label>
          <div className="input-row">
            <select
              id={`${id}-status`}
              value={status}
              onChange={(e) => setStatus(e.target.value as JobStatusFilter | "")}
            >
              <option value="">{t("admin.jobsFilterAll")}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {runStatusLabel(s)}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary" onClick={jobs.reload}>
              {t("admin.refresh")}
            </button>
          </div>
        </div>
      </div>
      {jobs.error && <ErrorMessage error={jobs.error} onRetry={jobs.reload} />}
      {!jobs.data && !jobs.error && <Loading />}
      {jobs.data && jobs.data.length === 0 && <p>{t("admin.jobsEmpty")}</p>}
      {jobs.data && jobs.data.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <caption className="visually-hidden">{t("admin.jobsCaption")}</caption>
            <thead>
              <tr>
                <th scope="col" className="num">
                  {t("admin.colId")}
                </th>
                <th scope="col">{t("admin.colKind")}</th>
                <th scope="col">{t("admin.colStatus")}</th>
                <th scope="col">{t("admin.colAttempts")}</th>
                <th scope="col">{t("admin.colRunAt")}</th>
                <th scope="col">{t("admin.colFinished")}</th>
                <th scope="col">{t("admin.colLastError")}</th>
                <th scope="col">{t("admin.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {jobs.data.map((j) => (
                <tr key={j.id}>
                  <th scope="row" className="num">
                    {j.id}
                  </th>
                  <td>
                    {j.kind}
                    <span className="cell-note">{payloadSummary(j.payload)}</span>
                  </td>
                  <td>
                    <span className={`run-status run-${j.status}`}>{runStatusLabel(j.status)}</span>
                  </td>
                  <td>{t("admin.attemptsValue", { attempts: j.attempts, max: j.max_attempts })}</td>
                  <td className="nowrap">{formatDateTime(j.run_at)}</td>
                  <td className="nowrap">{formatDateTime(j.finished_at)}</td>
                  <td>
                    {j.last_error ? (
                      <details>
                        <summary>{j.last_error.split("\n")[0]?.slice(0, 80)}</summary>
                        <pre className="error-text">{j.last_error}</pre>
                      </details>
                    ) : (
                      t("common.dash")
                    )}
                  </td>
                  <td>
                    {j.status === "failed" && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        disabled={busy === j.id}
                        onClick={() => void retry(j.id)}
                        aria-label={t("admin.retryLabel", { id: j.id })}
                      >
                        {t("admin.retry")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
